import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Settings2, X } from "lucide-react";

import {
  disableAnalytics,
  enableAnalytics,
  readCookieConsent,
  saveCookieConsent,
} from "@/lib/privacy";

export default function CookieConsent() {
  const initialConsent = readCookieConsent();
  const [consent, setConsent] = useState(initialConsent);
  const [open, setOpen] = useState(!initialConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [analytics, setAnalytics] = useState(initialConsent?.analytics === true);

  useEffect(() => {
    const current = readCookieConsent();

    if (current?.analytics) enableAnalytics();
    else disableAnalytics();

    const handleOpenSettings = () => {
      const latest = readCookieConsent();
      setConsent(latest);
      setAnalytics(latest?.analytics === true);
      setPreferencesOpen(true);
      setOpen(true);
    };

    const handleConsentChanged = (event) => {
      const next = event.detail || readCookieConsent();
      setConsent(next);
      setAnalytics(next?.analytics === true);
    };

    window.addEventListener("minga:open-cookie-settings", handleOpenSettings);
    window.addEventListener("minga:cookie-consent-changed", handleConsentChanged);

    return () => {
      window.removeEventListener("minga:open-cookie-settings", handleOpenSettings);
      window.removeEventListener("minga:cookie-consent-changed", handleConsentChanged);
    };
  }, []);

  const applyChoice = (analyticsAllowed) => {
    const next = saveCookieConsent({ analytics: analyticsAllowed });
    setConsent(next);
    setAnalytics(next.analytics);
    setPreferencesOpen(false);
    setOpen(false);
  };

  const savePreferences = () => applyChoice(analytics);

  if (!open) return null;

  return (
    <div
      className="fixed left-3 bottom-3 sm:left-5 sm:bottom-5 z-[120] w-[calc(100vw-24px)] sm:w-[390px] animate-fade-in"
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent"
    >
      <div className="border border-white/15 bg-ink-black/95 backdrop-blur-xl shadow-2xl rounded-sm overflow-hidden">
        <div className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex items-start gap-3">
              <div className="h-8 w-8 shrink-0 grid place-items-center border border-brand-yellow/35 text-brand-yellow">
                <Cookie className="h-4 w-4" />
              </div>

              <div className="min-w-0">
                <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] font-semibold text-brand-yellow">
                  Privacy choices
                </p>
                <h2
                  id="cookie-consent-title"
                  className="text-base sm:text-lg font-semibold tracking-tight mt-0.5 text-brand-white"
                >
                  Cookies & privacy
                </h2>
              </div>
            </div>

            {consent ? (
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="h-8 w-8 shrink-0 grid place-items-center border border-white/10 text-brand-gray hover:text-brand-white hover:border-white/25 transition-colors"
                aria-label="Close cookie settings"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>

          {!preferencesOpen ? (
            <>
              <p className="mt-3 text-xs sm:text-[13px] leading-relaxed text-brand-white/70">
                We use necessary storage for site functionality. Analytics stays off unless you choose to allow it.
              </p>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[10px] sm:text-[11px]">
                <Link
                  to="/privacy-policy"
                  className="text-brand-white/70 hover:text-brand-yellow transition-colors underline underline-offset-4"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cookie-policy"
                  className="text-brand-white/70 hover:text-brand-yellow transition-colors underline underline-offset-4"
                >
                  Cookie Policy
                </Link>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => applyChoice(false)}
                  className="min-h-9 border border-white/20 px-3 py-2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold text-brand-white hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                >
                  Reject
                </button>

                <button
                  type="button"
                  onClick={() => applyChoice(true)}
                  className="min-h-9 border border-brand-yellow bg-brand-yellow px-3 py-2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold text-ink-black hover:bg-transparent hover:text-brand-yellow transition-colors"
                >
                  Accept
                </button>

                <button
                  type="button"
                  onClick={() => setPreferencesOpen(true)}
                  className="col-span-2 min-h-8 inline-flex items-center justify-center gap-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold text-brand-white/70 hover:text-brand-yellow transition-colors"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Manage choices
                </button>
              </div>
            </>
          ) : (
            <div className="mt-4 space-y-3">
              <div className="border border-white/10 px-3 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs sm:text-[13px] font-semibold text-brand-white">
                    Strictly necessary
                  </p>
                  <p className="mt-1 text-[10px] sm:text-[11px] leading-relaxed text-brand-white/60">
                    Required to remember your privacy choice and support essential site functionality.
                  </p>
                </div>
                <span className="text-[8px] uppercase tracking-[0.16em] text-brand-green shrink-0 pt-0.5">
                  Always on
                </span>
              </div>

              <div className="border border-white/10 px-3 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0 pr-2">
                  <label
                    htmlFor="analytics-consent"
                    className="text-xs sm:text-[13px] font-semibold text-brand-white cursor-pointer"
                  >
                    Analytics
                  </label>
                  <p className="mt-1 text-[10px] sm:text-[11px] leading-relaxed text-brand-white/60">
                    Allows PostHog analytics to help us understand visits and improve the website.
                  </p>
                </div>

                <button
                  id="analytics-consent"
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics((value) => !value)}
                  className={`relative h-6 w-10 shrink-0 rounded-full border transition-colors ${
                    analytics
                      ? "bg-brand-yellow border-brand-yellow"
                      : "bg-white/5 border-white/20"
                  }`}
                >
                  <span
                    className={`absolute top-[3px] h-4 w-4 rounded-full transition-transform ${
                      analytics
                        ? "translate-x-[21px] bg-ink-black"
                        : "translate-x-[3px] bg-brand-white"
                    }`}
                  />
                  <span className="sr-only">Toggle analytics cookies</span>
                </button>
              </div>

              <div className="flex flex-wrap gap-3 text-[10px]">
                <Link
                  to="/privacy-policy"
                  className="text-brand-white/65 hover:text-brand-yellow transition-colors underline underline-offset-4"
                >
                  Privacy Policy
                </Link>
                <Link
                  to="/cookie-policy"
                  className="text-brand-white/65 hover:text-brand-yellow transition-colors underline underline-offset-4"
                >
                  Cookie Policy
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => applyChoice(false)}
                  className="min-h-9 border border-white/20 px-3 py-2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold text-brand-white hover:border-brand-yellow hover:text-brand-yellow transition-colors"
                >
                  Reject
                </button>

                <button
                  type="button"
                  onClick={savePreferences}
                  className="min-h-9 border border-brand-yellow bg-brand-yellow px-3 py-2 text-[9px] sm:text-[10px] uppercase tracking-[0.12em] font-semibold text-ink-black hover:bg-transparent hover:text-brand-yellow transition-colors"
                >
                  Save choices
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
